import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Button } from 'grommet';

const buttonProps = {
  normal: {
    width: '7.8125rem',
    height: '2.8125rem',
    fontSize: '1rem',
    lineHeight: 1.25,
  },
  small: {
    width: '6.95rem',
    height: '2.325rem',
    fontSize: '0.875rem',
    lineHeight: 1.28,
  },
  xsmall: {
    width: '5.55rem',
    height: '1.7rem',
    fontSize: '0.75rem',
    lineHeight: 1.29,
  },
  large: {
    width: '11.5rem',
    height: '3.25rem',
    fontSize: '1.1rem',
    lineHeight: 1.30,
  },
  xlarge: {
    width: '14rem',
    height: '3.25rem',
    fontSize: '1.1rem',
    lineHeight: 1.30,
  },
  newSearch: {
    width: '6rem',
    height: '2.325rem',
    fontSize: '0.875rem',
    lineHeight: 1.28,
  },
  requestFeature: {
    width: '5rem',
    height: '1.875rem',
    fontSize: '0.75rem',
    lineHeight: 1.29,
  },
  listRequestNew: {
    width: '8.15rem',
    height: '2.5rem',
    padding: '0.3rem 0.5rem',
    fontSize: '0.875rem',
    lineHeight: 1.28,
  },
  authSubmit: {
    width: '100%',
    height: '3.15rem',
    fontSize: '1.1rem',
    lineHeight: 1.30,
  },
  dynamicSmall: {
    width: '6.25rem',
    height: '2rem',
    fontSize: '1rem',
    lineHeight: 1.25,
  },
  dynamicLarge: {
    width: '9.25rem',
    height: '3.15rem',
    fontSize: '1.1rem',
    lineHeight: 1.30,
  },
  mobileMenu: {
    borderRadius: '0',
    width: '100%',
    height: '70px',
    fontSize: '20px',
    lineHeight: 1.30,
  },
  tableControls: {
    width: 'auto',
    height: 'auto',
    padding: '0.3rem 0.5rem',
    fontSize: '1rem',
    lineHeight: 1.25,
  },
};

const StyledButton = styled(Button)`
  display: flex;
  border-radius: ${({ dropDown, level }) => (dropDown ? '0' : (buttonProps[level]?.borderRadius || 'var(--app-radius)'))};
  padding: ${({ level }) => buttonProps[level]?.padding || 0};
  width: ${({ dropDown, width, level }) => (dropDown ? 'auto' : (width || buttonProps[level]?.width))};
  height: ${({ dropDown, height, level }) => (dropDown ? 'auto' : (height || buttonProps[level]?.height))};
  background: ${({ bgColor }) => bgColor || null};
  color: ${({ textColor }) => textColor || null};
  border-width: ${({ dropDown }) => (dropDown ? '0' : '1px')};
  font-size: ${({ textSize, level }) => textSize || buttonProps[level]?.fontSize};
  font-weight: ${({ fontWeight }) => fontWeight || 'normal'};
  line-height: ${({ lineHeight, level }) => lineHeight || buttonProps[level]?.lineHeight};
  letter-spacing: ${({ letterSpacing }) => letterSpacing || 'normal'};
  text-decoration: ${({ decoration }) => decoration || 'inherit'};
  align-items: center;
  justify-content: ${({ justify }) => justify || 'center'};
  ${({ justify }) => justify && justify === 'start' && css`
    padding-left: 30px;

    > div {
      width: 100%;
    }
  `}
  ${({ overrideHover, bgColor }) => overrideHover && bgColor && css`
    background: linear-gradient(#0000, rgb(0 0 0/40%)) top/100% 800%;
    background-color: ${bgColor};
    border: none;
    transition: all 0.25s ease-in-out;
  `}

  ${({ extraWide }) => extraWide && css`
    width: auto;
    padding: 0 0.85rem;
  `}

  &:hover {
    box-shadow: none;
    ${({ overrideHover, disabled }) => overrideHover && !disabled && ('background-position: bottom;')}
    ${({ dropDown, overrideHover }) => !dropDown && !overrideHover && css`
      color: ${({ color, hoverColor }) => hoverColor || (color === 'white' ? 'var(--button-primary-bg)' : 'white')};
      background: ${({ color }) => color || 'var(--button-primary-text)'};

      svg {
        stroke: ${({ color, hoverColor }) => hoverColor || (color === 'white' ? 'var(--button-primary-bg)' : 'white')};
      }
    `}
  }

  &:disabled {
    cursor: default;
    background-color: var(--button-disabled-bg);
    color: var(--button-disabled-text);
    border-color: #dcdedf;
    opacity: 1;

    svg {
      stroke: var(--button-disabled-text);
    }
  }
`;

const TableStyledButton = styled(StyledButton)`
  text-align: start;
  polyline { stroke-width: 3px; }
  background-color: ${({ bgColor }) => bgColor ?? 'var(--button-table-bg)'};
  border-radius: var(--app-radius);
  border: 1px solid transparent;
  padding: 0.3rem 0.5rem;
  font-size: ${({ textSize }) => textSize ?? '0.9rem'};

  ${({ noPadLeft }) => noPadLeft && `
    padding-left: 0px;
  `};

  &:hover {
    cursor: pointer;
    border: 1px solid ${({ focusHighlight }) => focusHighlight};
  }

  ${({ isOpen, focusHighlight }) => isOpen && `
    border: 1px solid ${focusHighlight};
  `}

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
  }
`;

const SortStyledButton = styled(StyledButton)`
  background: var(--page-input-bg);
  font-size: 0.8rem;
  padding: 0.3rem;
  border-radius: var(--app-radius);
  border: 1px solid var(--page-input-border);
  width: 6rem;
  height: var(--table-controls-height);

  &:focus {
    border: ${({ highlight }) => `1px solid ${highlight}`};
  }

  > div {
    border-radius: var(--app-radius);
    width: 100%;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: center;
  }

  svg {
    padding-top: 0.25rem;
  }
`;

const AppButton = ({
  level = null,
  icon = null,
  dropDown = false,
  tableButtonStyle = false,
  sortButtonStyle = false,
  ...rest
}) => {
  const shouldReverse = rest.overrideReverse ? false : dropDown;
  const selectButtonComponent = () => {
    if (tableButtonStyle) return TableStyledButton;
    if (sortButtonStyle) return SortStyledButton;
    return StyledButton;
  };
  const ButtonComponent = selectButtonComponent();

  return (
    <ButtonComponent
      level={level}
      reverse={shouldReverse}
      dropDown={dropDown}
      icon={icon}
      gap={icon ? '0.375rem' : null}
      {...rest}
    />
  );
};

AppButton.propTypes = {
  level: PropTypes.oneOf([
    'normal',
    'small',
    'xsmall',
    'large',
    'xlarge',
    'newSearch',
    'requestFeature',
    'listRequestNew',
    'authSubmit',
    'dynamicLarge',
    'dynamicSmall',
    'mobileMenu',
    'tableControls',
  ]),
  icon: PropTypes.node,
  dropDown: PropTypes.bool,
  tableButtonStyle: PropTypes.bool,
  sortButtonStyle: PropTypes.bool,
};

export default AppButton;
